import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Confirmation dialog used before any soft-delete or destructive action.
 *
 * Props:
 *  - open
 *  - onClose
 *  - onConfirm (async)
 *  - title
 *  - description
 *  - confirmLabel
 *  - tone: "danger" | "warning"
 *  - testid
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  tone = "danger",
  testid = "confirm-dialog",
}) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const accent = tone === "danger" ? "#C0392B" : "#E8A838";

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ background: "rgba(59,47,30,0.5)" }}
      onClick={onClose}
      data-testid={`${testid}-backdrop`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="se-card w-full max-w-md"
        data-testid={testid}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${accent}22`, color: accent }}
          >
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading" style={{ fontSize: 18 }}>{title}</h3>
            {description && (
              <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>{description}</p>
            )}
          </div>
          <button onClick={onClose} className="se-btn se-btn-ghost" style={{ padding: 6 }} data-testid={`${testid}-close`}>
            <X size={14} />
          </button>
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button className="se-btn se-btn-ghost" onClick={onClose} disabled={busy} data-testid={`${testid}-cancel`}>
            Cancel
          </button>
          <button
            className="se-btn"
            style={{ background: accent }}
            onClick={run}
            disabled={busy}
            data-testid={`${testid}-confirm`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
